
import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';

interface OrderItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
  image: string;
}

interface OrderData {
  items: OrderItem[];
  customerInfo: {
    name: string;
    email: string;
    phone: string;
    address: string;
    city: string;
    postalCode: string;
  };
  totalAmount: number;
  orderDate: string;
}

export default function BestelluebersichtPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [orderData, setOrderData] = useState<OrderData | null>(null);

  useEffect(() => {
    if (location.state?.orderData) {
      setOrderData(location.state.orderData);
    } else {
      // Wenn keine Daten vorhanden sind, zurück zur Bestellseite
      navigate('/bestellung');
    }
  }, [location.state, navigate]);

  if (!orderData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Bestelldaten werden geladen...</p>
        </div>
      </div>
    );
  }

  const handlePrint = () => {
    window.print();
  };

  const handleNewOrder = () => {
    navigate('/shop');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-black text-gray-900">Bestellübersicht</h1>
              <p className="text-gray-600 mt-1">Ihre Bestellung wurde erfolgreich übermittelt</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={handlePrint}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-2"
              >
                <i className="ri-printer-line"></i>
                Drucken
              </button>
              <button
                onClick={handleNewOrder}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-2"
              >
                <i className="ri-shopping-cart-line"></i>
                Neue Bestellung
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Bestelldetails */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
              <div className="bg-red-50 px-6 py-4 border-b">
                <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <i className="ri-shopping-bag-line text-red-600"></i>
                  Bestellte Artikel
                </h2>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Artikel</th>
                      <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900">Menge</th>
                      <th className="px-6 py-4 text-right text-sm font-semibold text-gray-900">Einzelpreis</th>
                      <th className="px-6 py-4 text-right text-sm font-semibold text-gray-900">Gesamtpreis</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {orderData.items.map((item, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-4">
                            <img
                              src={item.image}
                              alt={item.name}
                              className="w-16 h-16 object-cover rounded-lg border"
                            />
                            <div>
                              <h3 className="font-medium text-gray-900">{item.name}</h3>
                              <p className="text-sm text-gray-500">Art.-Nr: {item.id}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
                            {item.quantity} VE
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right font-medium text-gray-900">
                          CHF {item.price.toFixed(2)} pro VE
                        </td>
                        <td className="px-6 py-4 text-right font-bold text-gray-900">
                          CHF {(item.price * item.quantity).toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-50">
                    <tr>
                      <td colSpan={3} className="px-6 py-4 text-right font-bold text-lg text-gray-900">
                        Gesamtbetrag:
                      </td>
                      <td className="px-6 py-4 text-right font-bold text-xl text-red-600">
                        {orderData.totalAmount.toFixed(2)} CHF
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>

          {/* Kundeninformationen */}
          <div className="space-y-6">
            {/* Bestellinformationen */}
            <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
              <div className="bg-green-50 px-6 py-4 border-b">
                <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <i className="ri-information-line text-green-600"></i>
                  Bestellinformationen
                </h2>
              </div>
              <div className="p-6 space-y-4">
                <div className="flex items-center gap-3">
                  <i className="ri-calendar-line text-gray-400"></i>
                  <div>
                    <p className="text-sm text-gray-500">Bestelldatum</p>
                    <p className="font-medium text-gray-900">{orderData.orderDate}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <i className="ri-time-line text-gray-400"></i>
                  <div>
                    <p className="text-sm text-gray-500">Status</p>
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                      In Bearbeitung
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <i className="ri-shopping-cart-line text-gray-400"></i>
                  <div>
                    <p className="text-sm text-gray-500">Artikel gesamt</p>
                    <p className="font-medium text-gray-900">{orderData.items.length} Artikel</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Kundeninformationen */}
            <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
              <div className="bg-blue-50 px-6 py-4 border-b">
                <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <i className="ri-user-line text-blue-600"></i>
                  Kundendaten
                </h2>
              </div>
              <div className="p-6 space-y-4">
                <div className="flex items-start gap-3">
                  <i className="ri-user-fill text-gray-400 mt-1"></i>
                  <div>
                    <p className="text-sm text-gray-500">Name</p>
                    <p className="font-medium text-gray-900">{orderData.customerInfo.name}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-mail-line text-gray-400 mt-1"></i>
                  <div>
                    <p className="text-sm text-gray-500">E-Mail</p>
                    <p className="font-medium text-gray-900">{orderData.customerInfo.email}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-phone-line text-gray-400 mt-1"></i>
                  <div>
                    <p className="text-sm text-gray-500">Telefon</p>
                    <p className="font-medium text-gray-900">{orderData.customerInfo.phone}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-map-pin-line text-gray-400 mt-1"></i>
                  <div>
                    <p className="text-sm text-gray-500">Adresse</p>
                    <p className="font-medium text-gray-900">
                      {orderData.customerInfo.address}<br />
                      {orderData.customerInfo.postalCode} {orderData.customerInfo.city}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Kontakt */}
            <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
              <div className="bg-purple-50 px-6 py-4 border-b">
                <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <i className="ri-customer-service-line text-purple-600"></i>
                  Kontakt
                </h2>
              </div>
              <div className="p-6">
                <p className="text-gray-600 mb-4">
                  Bei Fragen zu Ihrer Bestellung können Sie uns gerne kontaktieren:
                </p>
                <div className="space-y-2">
                  <p className="flex items-center gap-2 text-sm">
                    <i className="ri-phone-fill text-purple-600"></i>
                    <span className="font-medium">+41 XX XXX XX XX</span>
                  </p>
                  <p className="flex items-center gap-2 text-sm">
                    <i className="ri-mail-fill text-purple-600"></i>
                    <span className="font-medium">info@snaeckomat.ch</span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Hinweise */}
        <div className="mt-8 bg-yellow-50 border border-yellow-200 rounded-xl p-6">
          <div className="flex items-start gap-3">
            <i className="ri-information-line text-yellow-600 text-xl mt-1"></i>
            <div>
              <h3 className="font-bold text-yellow-800 mb-2">Wichtige Hinweise</h3>
              <ul className="text-yellow-700 space-y-1 text-sm">
                <li>• Ihre Bestellung wird innerhalb von 24 Stunden bearbeitet</li>
                <li>• Sie erhalten eine Bestätigungs-E-Mail an die angegebene Adresse</li>
                <li>• Bei Rückfragen kontaktieren wir Sie telefonisch</li>
                <li>• Die Lieferung erfolgt je nach Verfügbarkeit der Artikel</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
